import { create } from 'zustand';
import { quotesService } from './quotesService';
import { roundMoneyToCents } from './utils/money';
import type {
  QuoteList,
  QuoteDetail,
  QuoteCreateRequest,
  QuoteNote,
  QuoteOutput,
} from './types';

interface QuotesState {
  quotes: QuoteList[];
  quotesLoading: boolean;
  quotesError: string | null;
  quotesPagination: {
    count: number;
    next: string | null;
    previous: string | null;
  };
  quotesFilters: {
    search: string;
    status: string | null;
    created_by: string | null;
    page: number;
    page_size: number;
  };

  currentQuote: QuoteDetail | null;
  currentQuoteLoading: boolean;
  currentQuoteError: string | null;

  fetchQuotes: (params?: {
    search?: string;
    status?: string;
    created_by?: string;
    page?: number;
    page_size?: number;
  }) => Promise<void>;
  setFilters: (filters: Partial<QuotesState['quotesFilters']>) => void;
  clearQuotes: () => void;

  fetchQuoteDetail: (id: number, force?: boolean) => Promise<void>;
  updateQuoteProgress: (id: number, progress: { expected_outputs_count?: number; outputs_count?: number; all_outputs_generated?: boolean; outputs_progress?: number; outputs?: QuoteOutput[]; generation_failed?: boolean; generation_error?: string }) => void;
  clearCurrentQuote: () => void;

  createQuote: (data: QuoteCreateRequest) => Promise<QuoteDetail>;
  updateQuote: (id: number, data: Partial<QuoteCreateRequest>) => Promise<QuoteDetail>;
  deleteQuote: (id: number) => Promise<void>;

  addNote: (quoteId: number, note: { notes: string }) => Promise<QuoteNote>;
  addNoteToCurrentQuote: (note: QuoteNote) => void;

  updateOutputField: (quoteId: number, outputId: number, field: string, value: string) => Promise<QuoteOutput | undefined>;
  patchQuoteOutput: (
    quoteId: number,
    outputId: number,
    data: Partial<QuoteOutput>
  ) => Promise<QuoteOutput | undefined>;
}

export const useQuotesStore = create<QuotesState>((set, get) => ({
  quotes: [],
  quotesLoading: false,
  quotesError: null,
  quotesPagination: {
    count: 0,
    next: null,
    previous: null,
  },
  quotesFilters: {
    search: '',
    status: null,
    created_by: null,
    page: 1,
    page_size: 10,
  },

  currentQuote: null,
  currentQuoteLoading: false,
  currentQuoteError: null,

  fetchQuotes: async (params) => {
    const currentState = get();
    
    set({ quotesLoading: true, quotesError: null });
    
    const startTime = Date.now();
    const minLoadingTime = 300;
    
    try {
      const filters = { ...currentState.quotesFilters, ...params };
      const serviceParams = {
        ...filters,
        status: filters.status ?? undefined,
        created_by: filters.created_by ?? undefined,
      };
      const response = await quotesService.getQuotes(serviceParams);
      
      const elapsed = Date.now() - startTime;
      const remainingTime = Math.max(0, minLoadingTime - elapsed);
      
      await new Promise(resolve => setTimeout(resolve, remainingTime));
      
      set({
        quotes: response.results,
        quotesPagination: {
          count: response.count,
          next: response.next,
          previous: response.previous,
        },
        quotesFilters: filters,
        quotesLoading: false,
      });
    } catch (error) {
      const elapsed = Date.now() - startTime;
      const remainingTime = Math.max(0, minLoadingTime - elapsed);
      
      await new Promise(resolve => setTimeout(resolve, remainingTime));
      
      set({
        quotesError: error instanceof Error ? error.message : 'Failed to fetch quotes',
        quotesLoading: false,
      });
    }
  },

  setFilters: (filters) => {
    set((state) => ({
      quotesFilters: { ...state.quotesFilters, ...filters },
    }));
  },

  clearQuotes: () => {
    set({
      quotes: [],
      quotesPagination: {
        count: 0,
        next: null,
        previous: null,
      },
    });
  },

  fetchQuoteDetail: async (id: number, force = false) => {
    const currentQuote = get().currentQuote;
    if (!force && currentQuote?.id === id) {
      return;
    }

    set({ currentQuoteLoading: true, currentQuoteError: null });
    
    const startTime = Date.now();
    const minLoadingTime = 300;
    
    try {
      const quote = await quotesService.getQuoteDetail(id);
      
      const elapsed = Date.now() - startTime;
      const remainingTime = Math.max(0, minLoadingTime - elapsed);
      
      await new Promise(resolve => setTimeout(resolve, remainingTime));
      
      set({
        currentQuote: quote,
        currentQuoteLoading: false,
      });
    } catch (error) {
      const elapsed = Date.now() - startTime;
      const remainingTime = Math.max(0, minLoadingTime - elapsed);
      
      await new Promise(resolve => setTimeout(resolve, remainingTime));
      
      set({
        currentQuoteError: error instanceof Error ? error.message : 'Failed to fetch quote',
        currentQuoteLoading: false,
      });
    }
  },

  clearCurrentQuote: () => {
    set({
      currentQuote: null,
      currentQuoteError: null,
    });
  },

  updateQuoteProgress: (id: number, progress) => {
    set((state) => {
      if (state.currentQuote?.id === id) {
        return {
          currentQuote: {
            ...state.currentQuote,
            expected_outputs_count: progress.expected_outputs_count ?? state.currentQuote.expected_outputs_count,
            outputs_count: progress.outputs_count ?? state.currentQuote.outputs_count,
            all_outputs_generated: progress.all_outputs_generated ?? state.currentQuote.all_outputs_generated,
            outputs_progress: progress.outputs_progress ?? state.currentQuote.outputs_progress,
            outputs: progress.outputs ?? state.currentQuote.outputs,
            generation_failed: progress.generation_failed ?? state.currentQuote.generation_failed,
            generation_error: progress.generation_error ?? state.currentQuote.generation_error,
          },
        };
      }
      return state;
    });
  },

  createQuote: async (data: QuoteCreateRequest) => {
    const quote = await quotesService.createQuote(data);
    set((state) => ({
      quotes: [quote, ...state.quotes],
      currentQuote: quote,
    }));
    return quote;
  },

  updateQuote: async (id: number, data: Partial<QuoteCreateRequest>) => {
    const quote = await quotesService.updateQuote(id, data);
    set((state) => ({
      quotes: state.quotes.map((q) => (q.id === id ? quote : q)),
      currentQuote: state.currentQuote?.id === id ? quote : state.currentQuote,
    }));
    return quote;
  },

  deleteQuote: async (id: number) => {
    await quotesService.deleteQuote(id);
    set((state) => ({
      quotes: state.quotes.filter((q) => q.id !== id),
      currentQuote: state.currentQuote?.id === id ? null : state.currentQuote,
    }));
  },

  addNote: async (quoteId: number, note: { notes: string }) => {
    const newNote = await quotesService.addNote(quoteId, note);
    get().addNoteToCurrentQuote(newNote);
    return newNote;
  },

  addNoteToCurrentQuote: (note: QuoteNote) => {
    set((state) => {
      if (!state.currentQuote) return state;
      return {
        currentQuote: {
          ...state.currentQuote,
          notes: [note, ...state.currentQuote.notes],
        },
      };
    });
  },

  updateOutputField: async (quoteId: number, outputId: number, field: string, value: string): Promise<QuoteOutput | undefined> => {
    const state = get();
    if (!state.currentQuote || state.currentQuote.id !== quoteId) return undefined;

    const output = state.currentQuote.outputs.find((o) => o.id === outputId);
    if (!output) return undefined;

    const { recalculateOutputFields } = await import('./utils/calculations');

    // Route the single edited field into the recalculator's `edits` object.
    // Anything not listed falls back to the current output value inside
    // recalculateOutputFields.
    const edits: Parameters<typeof recalculateOutputFields>[1] = {};
    switch (field) {
      case 'underwriting_discretion':
        edits.underwriting_discretion = value;
        break;
      case 'business_discretion':
        edits.business_discretion = value;
        break;
      case 'business_discretion_percentage_for_sas':
        edits.business_discretion_percentage_for_sas = value;
        break;
      case 'business_discretion_percentage_for_lms':
        edits.business_discretion_percentage_for_lms = value;
        break;
      case 'parento_commission_percentage_override':
        edits.parento_commission_percentage_override = value;
        break;
      case 'reinsurance_partner_override':
        edits.reinsurance_partner_override = value;
        break;
    }

    const updatedFields = recalculateOutputFields(output, edits);

    const prepareCurrencyForBackend = (fieldValue: string | null | undefined): string => {
      if (!fieldValue) return '0.00';
      const cleaned = String(fieldValue).replace(/[$,]/g, '').trim();
      const num = parseFloat(cleaned);
      if (isNaN(num)) return '0.00';
      return roundMoneyToCents(num).toFixed(2);
    };

    const parsePercentageForBackend = (percentageStr: string | null | undefined): string => {
      if (!percentageStr) return '0.00';
      const cleaned = String(percentageStr).replace(/%/g, '').trim();
      const num = parseFloat(cleaned);
      if (isNaN(num)) return '0.00';
      return num.toFixed(2);
    };

    // PATCH every derived value the FE just computed. BE persists them as-is —
    // no rater round-trip (Phase 3 of FE-only calc plan). The edited field
    // itself is sent in its raw form (string with % stripped).
    const updateData: Partial<Omit<QuoteOutput, 'id'>> = {
      // Edited field — keep raw string when it's an override (FE convention:
      // null/empty = "no override"); percentage-format the others.
      ...(field === 'parento_commission_percentage_override'
        ? {
            parento_commission_percentage_override:
              value === '' || value === null
                ? null
                : parsePercentageForBackend(value),
          }
        : field === 'reinsurance_partner_override'
        ? { reinsurance_partner_override: value }
        : { [field]: parsePercentageForBackend(value) }),
      // Derived currency fields (FE-computed; BE just persists).
      manual_premium: prepareCurrencyForBackend(updatedFields.manual_premium),
      quoted_premium: prepareCurrencyForBackend(updatedFields.quoted_premium),
      surplus_lines_tax: prepareCurrencyForBackend(updatedFields.surplus_lines_tax),
      stamping_fee: prepareCurrencyForBackend(updatedFields.stamping_fee),
      policy_issuance_fee: prepareCurrencyForBackend(updatedFields.policy_issuance_fee),
      quoted_premium_with_taxes: prepareCurrencyForBackend(updatedFields.quoted_premium_with_taxes),
      parento_commission: prepareCurrencyForBackend(updatedFields.parento_commission),
      quoted_sas_fee: prepareCurrencyForBackend(updatedFields.quoted_sas_fee),
      quoted_lms_fee: prepareCurrencyForBackend(updatedFields.quoted_lms_fee),
      total_program_cost_without_tax_and_stamping_fee: prepareCurrencyForBackend(updatedFields.total_program_cost_without_tax_and_stamping_fee),
      tax_and_stamping_fee: prepareCurrencyForBackend(updatedFields.tax_and_stamping_fee),
      total_program_costs_with_tax_stamping_fee: prepareCurrencyForBackend(updatedFields.total_program_costs_with_tax_stamping_fee),
      tax_credit_app_premium: prepareCurrencyForBackend(updatedFields.tax_credit_app_premium),
      tax_credit_amount: prepareCurrencyForBackend(updatedFields.tax_credit_amount),
      net_cost_after_tax_credit: prepareCurrencyForBackend(updatedFields.net_cost_after_tax_credit),
      net_cost_after_tax_credit_with_tax_fee: prepareCurrencyForBackend(updatedFields.net_cost_after_tax_credit_with_tax_fee),
      quoted_premium_pepm: prepareCurrencyForBackend(updatedFields.quoted_premium_pepm),
      quoted_sas_fee_pepm: prepareCurrencyForBackend(updatedFields.quoted_sas_fee_pepm),
      quoted_lms_fee_pepm: prepareCurrencyForBackend(updatedFields.quoted_lms_fee_pepm),
      quoted_premium_sas_fee_pepm: prepareCurrencyForBackend(updatedFields.quoted_premium_sas_fee_pepm),
      quoted_premium_sas_fee_pepm_with_tax_fee: prepareCurrencyForBackend(updatedFields.quoted_premium_sas_fee_pepm_with_tax_fee),
      parento_commissions_pepm: prepareCurrencyForBackend(updatedFields.parento_commissions_pepm),
      parento_total_revenue_pepm: prepareCurrencyForBackend(updatedFields.parento_total_revenue_pepm),
      // Derived percentage / partner fields.
      parento_commission_percentage: parsePercentageForBackend(updatedFields.parento_commission_percentage),
      pricing_loss_ratio_with_adj: parsePercentageForBackend(updatedFields.pricing_loss_ratio_with_adj),
      quoted_reinsurance_partner: updatedFields.quoted_reinsurance_partner ?? '',
    };

    const updatedOutput = await quotesService.updateQuoteOutput(quoteId, outputId, updateData);

    set((s) => {
      if (!s.currentQuote || s.currentQuote.id !== quoteId) return s;
      return {
        currentQuote: {
          ...s.currentQuote,
          outputs: s.currentQuote.outputs.map((o) =>
            o.id === outputId
              ? updatedOutput
              : o
          ),
        },
      };
    });

    return updatedOutput;
  },

  patchQuoteOutput: async (quoteId, outputId, data) => {
    const keys = Object.keys(data) as (keyof typeof data)[];
    if (keys.length === 0) return undefined;

    const quote = get().currentQuote;
    if (!quote || quote.id !== quoteId) return undefined;

    const previous = quote.outputs.find((o) => o.id === outputId);
    if (!previous) return undefined;

    const optimistic: QuoteOutput = { ...previous, ...data };

    set((s) => {
      if (!s.currentQuote || s.currentQuote.id !== quoteId) return s;
      return {
        currentQuote: {
          ...s.currentQuote,
          outputs: s.currentQuote.outputs.map((o) => (o.id === outputId ? optimistic : o)),
        },
      };
    });

    try {
      const updatedOutput = await quotesService.updateQuoteOutput(quoteId, outputId, data);
      const merged: QuoteOutput = { ...previous, ...updatedOutput };

      set((s) => {
        if (!s.currentQuote || s.currentQuote.id !== quoteId) return s;
        return {
          currentQuote: {
            ...s.currentQuote,
            outputs: s.currentQuote.outputs.map((o) => {
              if (o.id === outputId) return merged;
              if (data.is_sold_plan === true) return { ...o, is_sold_plan: false };
              return o;
            }),
          },
        };
      });

      return merged;
    } catch (e) {
      set((s) => {
        if (!s.currentQuote || s.currentQuote.id !== quoteId) return s;
        return {
          currentQuote: {
            ...s.currentQuote,
            outputs: s.currentQuote.outputs.map((o) => (o.id === outputId ? previous : o)),
          },
        };
      });
      throw e;
    }
  },
}));

